import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";

export type ConsumerOnboardingState = {
  welcomeCompleted: boolean;
  welcomeSkipped: boolean;
  welcomeStep: number;
  tourCompleted: boolean;
};

const DEFAULT_STATE: ConsumerOnboardingState = {
  welcomeCompleted: false,
  welcomeSkipped: false,
  welcomeStep: 0,
  tourCompleted: false,
};

export function consumerOnboardingKey(userId: string | undefined) {
  return ["consumer-onboarding", userId ?? "none"] as const;
}

export async function fetchConsumerOnboarding(
  userId: string,
): Promise<ConsumerOnboardingState> {
  const { data, error } = await supabase
    .from("consumer_onboarding_state")
    .select("welcome_completed, welcome_skipped, welcome_step, tour_completed")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return DEFAULT_STATE;

  return {
    welcomeCompleted: !!data.welcome_completed,
    welcomeSkipped: !!data.welcome_skipped,
    welcomeStep: Number(data.welcome_step ?? 0),
    tourCompleted: !!data.tour_completed,
  };
}

export function useConsumerOnboarding() {
  const { user, isGuest } = useAuth();
  const userId = user && !isGuest ? user.id : undefined;

  return useQuery({
    queryKey: consumerOnboardingKey(userId),
    queryFn: () => fetchConsumerOnboarding(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  });
}

type UpsertInput = {
  welcomeCompleted?: boolean;
  welcomeSkipped?: boolean;
  welcomeStep?: number;
  tourCompleted?: boolean;
};

export function useSaveConsumerOnboarding() {
  const { user, isGuest } = useAuth();
  const userId = user && !isGuest ? user.id : undefined;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: UpsertInput) => {
      if (!userId) throw new Error("Sign in required");

      const { data: existing } = await supabase
        .from("consumer_onboarding_state")
        .select("user_id, welcome_completed, welcome_skipped, welcome_step, tour_completed")
        .eq("user_id", userId)
        .maybeSingle();

      const payload = {
        user_id: userId,
        welcome_completed: input.welcomeCompleted ?? existing?.welcome_completed ?? false,
        welcome_skipped: input.welcomeSkipped ?? existing?.welcome_skipped ?? false,
        welcome_step: input.welcomeStep ?? existing?.welcome_step ?? 0,
        tour_completed: input.tourCompleted ?? existing?.tour_completed ?? false,
        updated_at: new Date().toISOString(),
      };

      if (existing) {
        const { error } = await supabase
          .from("consumer_onboarding_state")
          .update(payload)
          .eq("user_id", userId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("consumer_onboarding_state").insert(payload);
        if (error) throw error;
      }

      return {
        welcomeCompleted: !!payload.welcome_completed,
        welcomeSkipped: !!payload.welcome_skipped,
        welcomeStep: Number(payload.welcome_step ?? 0),
        tourCompleted: !!payload.tour_completed,
      } satisfies ConsumerOnboardingState;
    },
    onMutate: async (input) => {
      if (!userId) return;
      await queryClient.cancelQueries({ queryKey: consumerOnboardingKey(userId) });
      const previous = queryClient.getQueryData<ConsumerOnboardingState>(
        consumerOnboardingKey(userId),
      );
      queryClient.setQueryData<ConsumerOnboardingState>(consumerOnboardingKey(userId), {
        welcomeCompleted: input.welcomeCompleted ?? previous?.welcomeCompleted ?? false,
        welcomeSkipped: input.welcomeSkipped ?? previous?.welcomeSkipped ?? false,
        welcomeStep: input.welcomeStep ?? previous?.welcomeStep ?? 0,
        tourCompleted: input.tourCompleted ?? previous?.tourCompleted ?? false,
      });
      return { previous };
    },
    onError: (_err, _input, ctx) => {
      if (!userId || !ctx?.previous) return;
      queryClient.setQueryData(consumerOnboardingKey(userId), ctx.previous);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(consumerOnboardingKey(userId), data);
    },
  });

  return {
    save: mutation.mutateAsync,
    isSaving: mutation.isPending,
  };
}
